import { ReactElement, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import LocationInfo, { MyStoreTag } from '../../common/LocationInfo'
import { observer } from 'mobx-react-lite'
import { getInCartProductQty, useCartProduct } from 'cart'
import { ISeller } from '../../../../services/MultiSellerServicesProvider'
import { Vehicle } from '../../../../store/models/Vehicles'
import AddToCartButton from '../AddToCartButton'
import { QuantitySelector } from '../../../../ui-components/products/QuantitySelector'
import { useUserStore } from '../../../../store/user/UserStore'
import {
  NonCachedSearchType,
  ProductLocationModel,
  ProductModel,
} from 'interfaces'
import { LinearProgress } from '@mui/material'
import { SearchStore } from '../../../../store/search/SearchStore'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { LocationType } from 'src/helpers/locationType'
import { translateApiScheduleText } from 'src/helpers/utils'

interface Props {
  product: ProductModel
  vehicle: Vehicle
  seller: ISeller
  location: ProductLocationModel
  searchStore: SearchStore
  selected: boolean
  onLocationsUpdate: (locations: ProductLocationModel[]) => void
}

export const StoreCard = observer<Props>((props: Props): ReactElement => {
  const {
    seller,
    vehicle,
    product,
    location,
    selected = false,
    searchStore,
    onLocationsUpdate,
  } = props

  const userStore = useUserStore()
  const orderIfNotAvailablePref =
    userStore?.preferences?.findit_orderIfNotAvail === 'true'

  const cartProduct = useCartProduct({
    product,
    vehicle,
    sellerDetails: seller,
    locationId: location?.locationId,
  })

  const { updateQuantity, priceSpinner, setPriceSpinner } = cartProduct

  const isQtyAvailable = location.qtyAvailable > 0
  const isPrimary = location.locType === LocationType.PRIMARY
  const isViewOnly = location.locType === LocationType.VIEW_ONLY
  const orderIfNotAvailable = orderIfNotAvailablePref && isPrimary

  const isMyPlaceSeller = !seller.coverageKey

  const disableStore =
    (location.qtyAvailable === 0 &&
      (!isPrimary || !isMyPlaceSeller || !orderIfNotAvailablePref)) ||
    isViewOnly

  const inCartQty = useSelector((state: RootState) =>
    getInCartProductQty(
      state,
      vehicle,
      seller?.coverageKey ?? '',
      product,
      location.locationId
    )
  )

  const setInitialValueToOne =
    userStore?.preferences?.display_perCarQtyOne === 'true'

  const initialPerCarQty = setInitialValueToOne ? 1 : product.perCarQty || 0

  const displayMinQty =
    (location?.displayMinQty > location?.qtyAvailable
      ? location?.qtyAvailable
      : location?.displayMinQty) ?? 1

  const { lastNonCachedSearch } = searchStore
  const initialValueIfNotInCart =
    lastNonCachedSearch === NonCachedSearchType.searchByMultiPartsInquiry ||
    (lastNonCachedSearch === NonCachedSearchType.searchBySpecificParts &&
      location?.buyQty)
      ? location?.buyQty
      : Math.max(
          1,
          displayMinQty,
          location?.buyIncrement || 0,
          initialPerCarQty
        )

  const [selectorQty, setSelectorQty] = useState<number>(
    inCartQty && inCartQty > 0 ? inCartQty : initialValueIfNotInCart
  )

  const userModifiedRef = useRef(false)

  useEffect(() => {
    if (inCartQty > 0) {
      setSelectorQty(inCartQty)
      userModifiedRef.current = false
      return
    }

    if (userModifiedRef.current) return

    const partInfo = searchStore.getUpdatedPartInfoDetails(product)
    const updatedQtyMatchesLocation =
      partInfo?.updatedLocation?.locationId?.toString() ===
      location?.locationId?.toString()

    if (updatedQtyMatchesLocation && partInfo?.updatedQty > 0) {
      setSelectorQty(partInfo.updatedQty)
    } else {
      setSelectorQty(initialValueIfNotInCart)
    }
  }, [inCartQty, initialValueIfNotInCart, product, searchStore, location])

  const handleOnQtyChange = async (newQty: number): Promise<void> => {
    setPriceSpinner(true)
    userModifiedRef.current = true
    setSelectorQty(newQty)
    const { updatedLocations } = await updateQuantity(newQty, location)
    const updatedLoc = updatedLocations?.find(
      (loc) => loc.locationId?.toString() === location.locationId?.toString()
    )
    if (
      updatedLoc &&
      newQty > updatedLoc.qtyAvailable &&
      !orderIfNotAvailable
    ) {
      const { qtyAvailable = 0, minQty = 1 } = updatedLoc
      const correctedQty = qtyAvailable === 0 ? minQty : qtyAvailable
      setSelectorQty(correctedQty)
    }
    onLocationsUpdate(updatedLocations)
  }

  const disablePlusStep =
    !isPrimary && selectorQty + location?.buyIncrement > location?.qtyAvailable

  return (
    <StoreWrapper>
      <FlexColumnDiv>
        <LocationInfo
          disableStore={disableStore}
          isQtyAvailable={isQtyAvailable}
          selected={selected}
          location={location}
        />
        {isPrimary && <MyStoreTag />}
      </FlexColumnDiv>

      <ActionsColumn>
        <QuantitySelector
          orderIfNotAvailable={orderIfNotAvailable && isPrimary}
          onChange={handleOnQtyChange}
          onRemove={() => undefined}
          minQty={Math.max(location?.minQty, location?.buyIncrement || 0)}
          stepSize={location?.buyIncrement}
          onInputHoverTooltipMsg={translateApiScheduleText(location?.schedule)}
          orderNumber={product.orderNumber}
          isStoked={product.isStocked}
          value={selectorQty?.toString()}
          setValue={(val) => setSelectorQty(Number(val))}
          isCondensedView={false}
          disabled={disableStore}
          disablePlusStep={disablePlusStep}
          isLoading={priceSpinner}
        />
        {priceSpinner && <LinearProgress />}
        <AddToCartButton
          seller={seller}
          vehicle={vehicle}
          cartProduct={cartProduct}
          location={location}
          qty={selectorQty}
          disableButton={disableStore}
        />
      </ActionsColumn>
    </StoreWrapper>
  )
})

const FlexColumnDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const ActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 168px;
  flex-shrink: 0;
`

const StoreWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 1rem;
  padding-top: 1rem;
  border-bottom: 1px solid #d3d2cf;
  text-align: start;
`
